import type { Dispatch, SetStateAction } from 'react';
import { normalizeAiosMultitaskToolInput } from '@/utils/command-tools';
import { ExtendedChatMessage } from './chat-types';
import { ContentSegment, ParsedTool } from './message-parser';
import { serializeToolResultContent } from './message-utils';

export type SSEEventHandler = (event: any) => void;

export interface ContentBlock {
    index: number;
    type: 'text' | 'thinking' | 'tool_use' | 'tool_result' | 'observation';
    content: string;
    toolId?: string;
    toolName?: string;
    toolInput?: any;
    toolOutput?: string;
}

/**
 * Split raw SSE output on newlines, strip `data:` prefixes, JSON-parse each frame
 * and dispatch to `onEvent`. Malformed frames are silently dropped — SSE tolerates
 * partial lines between chunks.
 */
export function processSSEChunk(chunk: string, onEvent: SSEEventHandler) {
    const lines = chunk.split('\n').filter(line => line.trim());
    for (const line of lines) {
        try {
            let jsonStr = line.trim();
            if (jsonStr.startsWith('data:')) {
                jsonStr = jsonStr.slice(5).trim();
            }
            if (jsonStr && jsonStr.startsWith('{')) {
                const event = JSON.parse(jsonStr);
                onEvent(event);
            }
        } catch {
            // Malformed line; skip silently.
        }
    }
}

interface CreateEventHandlerOptions {
    /** Assistant message id this stream is updating (top-level or nested branch). */
    systemMessageId: string;
    setMessages: Dispatch<SetStateAction<ExtendedChatMessage[]>>;
    onSessionTitle?: (sessionId: string, title: string) => void;
    /** When flipped to `true`, future events are ignored — used to freeze UI updates on session switch. */
    cancelledRef?: { current: boolean };
    setStreamingPhase?: Dispatch<SetStateAction<'working' | 'responding'>>;
    /**
     * Optional temp user message id. When `message_start` arrives with `user_message_id`,
     * the message's `id` is replaced with the real server id so subsequent refetches
     * dedupe naturally and downstream consumers can stop juggling temp vs real ids.
     */
    userMessageId?: string;
    /**
     * Fires once `message_start` resolves the real server id for the assistant message —
     * the caller (e.g. `finalizeMessage`) needs the new id because the message's `id`
     * field has been swapped out from under it.
     */
    onAssistantIdResolved?: (realId: string) => void;
    /** Same as `onAssistantIdResolved` but for the paired user message. */
    onUserIdResolved?: (realId: string) => void;
}

/**
 * Build an SSE event handler that incrementally populates an assistant chat message
 * (plus optionally its paired user message) from streaming blocks. Each handler owns
 * its own block Map so concurrent streams don't clobber each other's state.
 */
export function createEventHandler({
    systemMessageId,
    setMessages,
    onSessionTitle,
    cancelledRef,
    setStreamingPhase,
    userMessageId,
    onAssistantIdResolved,
    onUserIdResolved,
}: CreateEventHandlerOptions): SSEEventHandler {
    // Track the live id of the assistant/user rows. `message_start` replaces the temp ids
    // with the real server ids — every later event has to look up the message by the
    // resolved id, otherwise text_delta updates would miss after the swap.
    let currentAssistantId = systemMessageId;
    let currentUserId = userMessageId;
    // Per-handler block state — prevents concurrent streams (send + reconnect) from clobbering each other.
    const blocks = new Map<number, ContentBlock>();
    const buildSegments = (): ContentSegment[] => {
        const segments: ContentSegment[] = [];
        const sortedBlocks = Array.from(blocks.values()).sort((a, b) => a.index - b.index);

        const toolOutputs = new Map<number, string>();
        for (let i = 0; i < sortedBlocks.length; i++) {
            const block = sortedBlocks[i];
            if (block.type === 'observation' && block.content) {
                for (let j = i - 1; j >= 0; j--) {
                    if (sortedBlocks[j].type === 'tool_use') {
                        toolOutputs.set(sortedBlocks[j].index, block.content);
                        break;
                    }
                }
            }
            
            if (block.type === 'tool_result' && block.content) {
                for (let j = i - 1; j >= 0; j--) {
                    if (sortedBlocks[j].type === 'tool_use') {
                        toolOutputs.set(sortedBlocks[j].index, block.content);
                        break;
                    }
                }
            }
            if (block.type === 'tool_use' && block.toolOutput != null && block.toolOutput !== '') {
                toolOutputs.set(block.index, block.toolOutput);
            }
        }

        for (const block of sortedBlocks) {
            if (block.type === 'text') {
                if (block.content) {
                    segments.push({ type: 'text', content: block.content });
                }
            } else if (block.type === 'tool_use') {
                const formattedName = (block.toolName || 'unknown').toLowerCase().replace(/\s+/g, '_');
                const output = toolOutputs.get(block.index);
                const tool: ParsedTool = {
                    toolCallId: block.toolId || `tool-${block.index}`,
                    type: `tool-${formattedName}`,
                    state: output ? 'output-available' : 'input-available',
                    input: block.toolInput,
                    output: output,
                    position: segments.filter(s => s.type === 'tool').length,
                };
                segments.push({ type: 'tool', tool });
            }
        }
        return segments;
    };

    /** Patch the target assistant message whether it is top-level or nested in `branches`. */
    const updateMessage = (updater: (msg: ExtendedChatMessage) => ExtendedChatMessage) => {
        setMessages(prev => prev.map(msg => {
            if (msg.id === currentAssistantId) {
                return updater(msg);
            }
            if (msg.branches) {
                const branchIndex = msg.branches.findIndex(b => b.id === currentAssistantId);
                if (branchIndex !== -1) {
                    const updatedBranches = [...msg.branches];
                    updatedBranches[branchIndex] = updater(updatedBranches[branchIndex]);
                    return { ...msg, branches: updatedBranches };
                }
            }
            return msg;
        }));
    };

    const syncSegments = () => {
        const segments = buildSegments();
        const tools = segments.filter(s => s.type === 'tool').map(s => s.tool!);
        updateMessage(msg => ({
            ...msg,
            contentSegments: segments,
            tools: tools.length > 0 ? tools : msg.tools,
            isStreaming: true,
        }));
    };

    let nextBlockIndex = 0;
    let currentTextBlockIndex: number | null = null;
    const pendingToolResults = new Map<string, string>();

    return (event: any) => {
        if (cancelledRef?.current) return;

        if (event.type === 'message_start' && (event.message?.id ?? event.id)) {
            nextBlockIndex = 0;
            currentTextBlockIndex = null;
            pendingToolResults.clear();
            blocks.clear();
            const realMessageId: string = event.message?.id ?? event.id;
            const realUserMessageId: string | null =
                event.user_message_id ?? event.message?.user_message_id ?? null;
            // Snapshot the ids the handler is currently tracking before we mutate state —
            // the setMessages map runs after the let-bindings would otherwise advance, and
            // we need the old ids to find the rows.
            const prevAssistantId = currentAssistantId;
            const prevUserId = currentUserId;
            setMessages(prev => prev.map(msg => {
                if (msg.id === prevAssistantId) {
                    // Stamp parent_message_id so the merge/sort logic can anchor this row
                    // next to its user message even when the server refetch hasn't returned
                    // the assistant row yet (it stays in `localOnly` with no server match,
                    // so propagating from `serverMsg` later wouldn't help — we have to set
                    // it here, while we have `realUserMessageId` in scope).
                    return {
                        ...msg,
                        id: realMessageId,
                        dbId: realMessageId,
                        ...(realUserMessageId && { parent_message_id: realUserMessageId }),
                    };
                }
                if (prevUserId && realUserMessageId && msg.id === prevUserId) {
                    return { ...msg, id: realUserMessageId, dbId: realUserMessageId };
                }
                if (msg.branches) {
                    return {
                        ...msg,
                        branches: msg.branches.map(branch =>
                            branch.id === prevAssistantId
                                ? {
                                    ...branch,
                                    id: realMessageId,
                                    dbId: realMessageId,
                                    ...(realUserMessageId && { parent_message_id: realUserMessageId }),
                                }
                                : branch,
                        ),
                    };
                }
                return msg;
            }));
            currentAssistantId = realMessageId;
            onAssistantIdResolved?.(realMessageId);
            if (realUserMessageId) {
                currentUserId = realUserMessageId;
                onUserIdResolved?.(realUserMessageId);
            }
        }

        if (event.type === 'tool_call') {
            setStreamingPhase?.('working');
            currentTextBlockIndex = null;
            const name = event.name ?? 'unknown';
            const input = normalizeAiosMultitaskToolInput(
                name,
                (event.input ?? {}) as Record<string, unknown>,
            );
            const rawId = event.tool_call_id ?? event.id ?? `tool-${nextBlockIndex}`;
            const toolCallId = String(rawId).trim();
            blocks.set(nextBlockIndex, {
                index: nextBlockIndex,
                type: 'tool_use',
                content: '',
                toolId: toolCallId,
                toolName: name,
                toolInput: input,
            });
            // Tool-result may arrive before its matching tool_call: drain the queue here.
            const pendingOutput = pendingToolResults.get(toolCallId);
            if (pendingOutput !== undefined) {
                const block = blocks.get(nextBlockIndex);
                if (block && block.type === 'tool_use') block.toolOutput = pendingOutput;
                pendingToolResults.delete(toolCallId);
            }
            nextBlockIndex += 1;
            syncSegments();
        }

        if (event.type === 'tool_result') {
            const rawId = event.tool_call_id ?? event.id ?? null;
            const toolCallId = rawId != null ? String(rawId).trim() : null;
            const payload = event.content ?? event.output;
            const outputStr = serializeToolResultContent(payload);
            const toolUseBlocks = Array.from(blocks.values())
                .filter((b): b is ContentBlock & { type: 'tool_use' } => b.type === 'tool_use')
                .sort((a, b) => a.index - b.index);
            const normalizeId = (id: string | undefined) => (id != null ? String(id).trim() : '');
            let targetBlock = toolCallId
                ? toolUseBlocks.find(b => normalizeId(b.toolId) === toolCallId)
                : null;
            if (!targetBlock) {
                targetBlock = toolUseBlocks.find(b => !b.toolOutput) ?? null;
            }
            if (targetBlock) {
                targetBlock.toolOutput = outputStr;
                syncSegments();
            } else if (toolCallId) {
                pendingToolResults.set(toolCallId, outputStr);
            }
        }

        if (event.type === 'text_delta' && event.text != null) {
            setStreamingPhase?.('responding');
            let textBlock =
                currentTextBlockIndex !== null ? blocks.get(currentTextBlockIndex) : null;
            if (!textBlock || textBlock.type !== 'text') {
                textBlock = { index: nextBlockIndex, type: 'text' as const, content: '' };
                blocks.set(nextBlockIndex, textBlock);
                currentTextBlockIndex = nextBlockIndex;
                nextBlockIndex += 1;
            }
            textBlock.content += event.text;
            syncSegments();
        }

        if (event.type === 'text' && event.text != null) {
            if (currentTextBlockIndex !== null) {
                const textBlock = blocks.get(currentTextBlockIndex);
                if (textBlock && textBlock.type === 'text') {
                    textBlock.content = event.text;
                    syncSegments();
                    return;
                }
            }
            const textBlock: ContentBlock = { index: nextBlockIndex, type: 'text', content: event.text };
            blocks.set(nextBlockIndex, textBlock);
            currentTextBlockIndex = nextBlockIndex;
            nextBlockIndex += 1;
            syncSegments();
        }

        if (event.type === 'session_title') {
            onSessionTitle?.(event.session_id, event.title);
            return;
        }

        if (event.type === 'content_block_start') {
            const block = event.content_block;
            const index = event.index;

            if (block.type === 'text') {
                blocks.set(index, { index, type: 'text', content: block.text || '' });
                syncSegments();
            } else if (block.type === 'thinking') {
                blocks.set(index, { index, type: 'thinking', content: block.thinking || '' });
                if (block.thinking) {
                    updateMessage(msg => ({ ...msg, reasoning: block.thinking, isStreaming: true }));
                }
            } else if (block.type === 'tool_use') {
                blocks.set(index, {
                    index,
                    type: 'tool_use',
                    content: '',
                    toolId: block.id,
                    toolName: block.name,
                    toolInput: block.input,
                });
                syncSegments();
            } else if (block.type === 'tool_result') {
                blocks.set(index, { index, type: 'tool_result', content: block.content || '' });
            } else if (block.type === 'observation') {
                const observationContent = block.content || block.observation || '';
                blocks.set(index, { index, type: 'observation', content: observationContent });
                syncSegments();
            }
        }

        if (event.type === 'content_block_delta') {
            const index = event.index;
            const delta = event.delta;
            const block = blocks.get(index);

            if (delta.type === 'text_delta' && block) {
                block.content += delta.text;
                syncSegments();
            } else if (delta.type === 'thinking_delta' && block) {
                block.content += delta.thinking;
                const allThinking = Array.from(blocks.values())
                    .filter(b => b.type === 'thinking')
                    .sort((a, b) => a.index - b.index)
                    .map(b => b.content)
                    .join('\n');
                updateMessage(msg => ({ ...msg, reasoning: allThinking, isStreaming: true }));
            } else if (delta.type === 'input_json_delta' && block && block.type === 'tool_use') {
                // Reserved for incremental tool-input deltas; no-op today.
            } else if (delta.type === 'tool_result_delta' && block) {
                block.content += delta.result || '';
            } else if (delta.type === 'observation_delta' && block) {
                const deltaContent = delta.content || delta.observation || delta.text || '';
                block.content += deltaContent;
                syncSegments();
            }
        }

        if (event.type === 'error') {
            const errorText =
                typeof event.text === 'string'
                    ? event.text
                    : typeof event.message === 'string'
                      ? event.message
                      : '';
            if (!errorText) return;

            const segments = buildSegments();
            updateMessage((msg) => ({
                ...msg,
                streamError: errorText,
                contentSegments: [...segments, { type: 'error', content: errorText }],
                isStreaming: false,
            }));
            return;
        }

        if (event.type === 'message_stop') {
            updateMessage(msg => ({ ...msg, isStreaming: false }));
        }
    };
}
